import React, { useEffect, useState } from 'react';
import { Context as ResponsiveContext } from 'react-responsive';
import serverRenderSettings from '@fogia/core-web/config/ssr';

interface SsrBreakpointProviderProps {
    children: React.ReactNode;
}

/**
 * Provides the same breakpoint width used during SSR for the initial client render,
 * preventing hydration mismatches in components that use `useBreakpoint`.
 *
 * Phase 1 (initial render): Reads `window.isMobile` (serialized by the server) and provides
 * the matching breakpoint width via ResponsiveContext. All `useBreakpoint` calls match the server.
 *
 * Phase 2 (after mount): Removes the provider so `react-responsive` uses the actual viewport width.
 */
const SsrBreakpointProvider = ({ children }: SsrBreakpointProviderProps) => {
    const [isHydrated, setIsHydrated] = useState(false);

    const ssrWidth = (() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const { isMobile } = window as any;

        return isMobile
            ? serverRenderSettings.renderBreakpoints.mobile
            : serverRenderSettings.renderBreakpoints.desktop;
    })();

    useEffect(() => {
        // After mount, remove the provider so `react-responsive` uses the actual viewport width
        setIsHydrated(true);
    }, []);

    if (isHydrated) {
        return <>{children}</>;
    }

    return <ResponsiveContext.Provider value={{ width: ssrWidth }}>{children}</ResponsiveContext.Provider>;
};

export default SsrBreakpointProvider;
